import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { confirmDialog } from "../components/ConfirmDialog";
import * as XLSX from "xlsx";
import {
  Wallet,
  CreditCard,
  Download,
  Search,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Copy,
  FileText,
  RefreshCw,
  User,
  Home,
  Calendar,
  Eye,
  ExternalLink,
  Receipt,
  Layers,
  CheckCircle,
  Building2,
  ChevronRight,
  ChevronLeft,
  X,
  Tag,
  ShieldCheck,
  Info,
  Coins,
  DollarSign,
  Trash2
} from "lucide-react";
import Sidebar, { clearSession } from "../components/Sidebar";
import { applyBillingEnumText } from "../utils/enums";
import { isPhaseAdmin } from "../utils/role";

// --- TARGET HOUSES FROM USER LIST ---
export const TARGET_HOUSES = [
  { phase: "Phase 4", number: "33" },
  { phase: "Phase 4", number: "34" },
  { phase: "Phase 4", number: "35" },
  { phase: "Phase 3", number: "25" },
  { phase: "Phase 4", number: "30" },
  { phase: "Phase 4", number: "46" },
  { phase: "Phase 4", number: "44" },
  { phase: "Phase 4", number: "43" },
  { phase: "Phase 2", number: "C11" },
  { phase: "Phase 2", number: "B5" },
  { phase: "Phase 4", number: "1" },
  { phase: "Phase 2", number: "B10" },
  { phase: "Phase 2", number: "B12" },
  { phase: "Phase 2", number: "B1" },
  { phase: "Phase 4", number: "32" },
  { phase: "Phase 4", number: "42" },
  { phase: "Phase 4", number: "24" },
  { phase: "Phase 4", number: "40" },
  { phase: "Phase 4", number: "36" },
  { phase: "Phase 2", number: "A7" },
  { phase: "Phase 4", number: "25" },
  { phase: "Phase 4", number: "26" },
  { phase: "Phase 4", number: "2" },
  { phase: "Phase 4", number: "47" },
  { phase: "Phase 4", number: "6" },
  { phase: "Phase 4", number: "5" },
  { phase: "Phase 4", number: "22" },
  { phase: "Phase 4", number: "21" },
  { phase: "Phase 4", number: "28" },
];

const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

function safeJsonParse(text) {
  try { return applyBillingEnumText(JSON.parse(text)); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

async function fetchWithTimeout(url, options = {}, timeoutMs = 45000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

function fmtMoney(amount) {
  const num = typeof amount === "string" ? parseFloat(amount.replace(/[^0-9.-]+/g, "")) : Number(amount || 0);
  if (isNaN(num)) return "₦0";
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0
  }).format(num).replace("NGN", "₦");
}

function parseNumericAmount(amount) {
  if (typeof amount === "number") return amount;
  if (!amount) return 0;
  const cleaned = String(amount).replace(/[^0-9.-]+/g, "");
  const val = parseFloat(cleaned);
  return isNaN(val) ? 0 : val;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return String(iso);
  }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return String(iso);
  }
}

function getInitials(name) {
  if (!name) return "T";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function normString(str) {
  return String(str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Gentle rate-limited concurrency runner (max 4 concurrent requests)
async function mapConcurrent(items, limit, fn) {
  const results = [];
  const executing = [];
  for (const item of items) {
    const p = Promise.resolve().then(() => fn(item));
    results.push(p);
    if (limit <= items.length) {
      const e = p.then(() => {
        const idx = executing.indexOf(e);
        if (idx !== -1) executing.splice(idx, 1);
      });
      executing.push(e);
      if (executing.length >= limit) {
        await Promise.race(executing);
      }
    }
  }
  return Promise.all(results);
}

function PagePreloader({ visible }) {
  return (
    <div className={`page-preloader-overlay ${visible ? "is-visible" : ""}`} aria-hidden={!visible}>
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

export default function SeePayment() {
  const navigate = useNavigate();
  const session = getSession();
  const estateId = session?.estateId || "";

  // Data Loading & Progress states
  const [loading, setLoading] = useState(true);
  const [checkingTargetHouses, setCheckingTargetHouses] = useState(false);
  const [progressStatus, setProgressStatus] = useState("");

  // Target Houses Results (Live)
  const [targetHouseResults, setTargetHouseResults] = useState([]);
  const [allEstateBuildings, setAllEstateBuildings] = useState([]);
  const [allEstateTenants, setAllEstateTenants] = useState([]);
  const [estateInvoiceComponents, setEstateInvoiceComponents] = useState([]);
  const [buildingInvoiceComponentsCache, setBuildingInvoiceComponentsCache] = useState({});

  // Active Tab View: "targetHouses" | "allInvoices" | "estateComponents"
  const [activeTab, setActiveTab] = useState("targetHouses");

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all");

  // Modal State for Viewing Detailed Tenant / Payment / Invoices & Components Info
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalBuildingComponents, setModalBuildingComponents] = useState([]);
  const [loadingModalComponents, setLoadingModalComponents] = useState(false);
  const [copiedRef, setCopiedRef] = useState(false);

  // Table pagination
  const [tablePage, setTablePage] = useState(1);
  const tablePageSize = 15;

  // Initialize Metronic UI
  useEffect(() => {
    const body = document.getElementById("kt_app_body") || document.body;
    body.id = "kt_app_body";
    body.className = "app-default";
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 50);
    return () => clearTimeout(t);
  }, []);

  // Fetch Building Invoice Components helper
  const fetchBuildingComponents = useCallback(async (buildingId) => {
    if (!estateId || !buildingId) return [];
    if (buildingInvoiceComponentsCache[buildingId]) {
      return buildingInvoiceComponentsCache[buildingId];
    }
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/buildings/${buildingId}/invoice-components`);
      if (res && res.ok) {
        const data = parseArrayData(safeJsonParse(await res.text()));
        setBuildingInvoiceComponentsCache(prev => ({ ...prev, [buildingId]: data }));
        return data;
      }
    } catch (err) {
      console.warn(`Could not fetch components for building ${buildingId}:`, err);
    }
    return [];
  }, [estateId, buildingInvoiceComponentsCache]);

  const handleDeleteEstateComponent = async (comp) => {
    const componentId = comp.estateInvoiceComponentId || comp.id;
    if (!estateId || !componentId) return toast.error("Component information is missing.");
    const name = comp.displayName || comp.defaultDisplayName || "this component";
    if (!(await confirmDialog({ title: "Remove Invoice Component", message: `Remove "${name}" from the estate invoice components?`, confirmLabel: "Remove" }))) return;
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/invoice-components/${componentId}`, { method: "DELETE" });
      if (!res.ok) {
        const json = safeJsonParse(await res.text());
        throw new Error(json?.message || "Failed to remove invoice component.");
      }
      setEstateInvoiceComponents(prev => prev.filter(c => (c.estateInvoiceComponentId || c.id) !== componentId));
      toast.success("Invoice component removed.");
    } catch (err) {
      toast.error(err.message || "Failed to remove invoice component.");
    }
  };

  const handleDeleteBuildingComponent = async (buildingId, bComp) => {
    const assignmentId = bComp.assignmentId || bComp.id;
    if (!estateId || !buildingId || !assignmentId) return toast.error("Component information is missing.");
    const name = bComp.displayName || bComp.defaultDisplayName || "this component";
    if (!(await confirmDialog({ title: "Remove Invoice Component", message: `Remove "${name}" from this building?`, confirmLabel: "Remove" }))) return;
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/buildings/${buildingId}/invoice-components/${assignmentId}`, { method: "DELETE" });
      if (!res.ok) {
        const json = safeJsonParse(await res.text());
        throw new Error(json?.message || "Failed to remove invoice component.");
      }
      const keep = c => (c.assignmentId || c.id) !== assignmentId;
      setModalBuildingComponents(prev => prev.filter(keep));
      setBuildingInvoiceComponentsCache(prev => prev[buildingId] ? { ...prev, [buildingId]: prev[buildingId].filter(keep) } : prev);
      toast.success("Invoice component removed from building.");
    } catch (err) {
      toast.error(err.message || "Failed to remove invoice component.");
    }
  };

  // Check target houses and live payment/invoice data
  const checkTargetHouses = useCallback(async () => {
    if (!estateId) {
      setLoading(false);
      return;
    }

    setCheckingTargetHouses(true);
    setProgressStatus("Fetching estate buildings, tenant records & invoice components catalog...");

    try {
      // 1. Fetch estate buildings, upcoming rent, all tenants & estate invoice components
      const [buildingsRes, upcomingRes, tenantsRes, estateCompRes] = await Promise.all([
        authFetch(`${API_BASE}/estates/${estateId}/buildings`).catch(() => null),
        authFetch(`${API_BASE}/estates/${estateId}/tenants/upcoming-rent?daysAhead=365&pageIndex=0&pageSize=1000`).catch(() => null),
        authFetch(`${API_BASE}/estates/${estateId}/tenants/with-dependents?pageIndex=0&pageSize=1000`).catch(() => null),
        // The estate invoice-component catalog is estate-admin only.
        isPhaseAdmin() ? Promise.resolve(null) : authFetch(`${API_BASE}/estates/${estateId}/invoice-components`).catch(() => null)
      ]);

      let buildingsList = [];
      if (buildingsRes && buildingsRes.ok) {
        buildingsList = parseArrayData(safeJsonParse(await buildingsRes.text()));
      }

      let upcomingItems = [];
      if (upcomingRes && upcomingRes.ok) {
        upcomingItems = parseArrayData(safeJsonParse(await upcomingRes.text()));
      }

      let allTenants = [];
      if (tenantsRes && tenantsRes.ok) {
        allTenants = parseArrayData(safeJsonParse(await tenantsRes.text()));
      }

      let estateComps = [];
      if (estateCompRes && estateCompRes.ok) {
        estateComps = parseArrayData(safeJsonParse(await estateCompRes.text()));
      }

      setAllEstateBuildings(buildingsList);
      setAllEstateTenants(allTenants);
      setEstateInvoiceComponents(estateComps);

      setProgressStatus(`Checking payments, rent invoices & components for the ${TARGET_HOUSES.length} requested houses...`);

      // 2. Map and verify each target house
      let checkedCount = 0;
      const bCompCache = {};

      const results = await mapConcurrent(TARGET_HOUSES, 4, async (target) => {
        const targetPhaseNorm = normString(target.phase);
        const targetNumNorm = normString(target.number);

        // Find matched building from estate
        const matchedBuilding = buildingsList.find(b => {
          const bPhaseNorm = normString(b.phaseName || b.phase);
          const bNumNorm = normString(b.number || b.buildingNumber);
          return (
            (bPhaseNorm.includes(targetPhaseNorm) || targetPhaseNorm.includes(bPhaseNorm)) &&
            (bNumNorm === targetNumNorm || bNumNorm === `house${targetNumNorm}`)
          );
        });

        // Find assigned tenant
        let matchedTenant = null;
        let tenantId = null;

        if (matchedBuilding) {
          tenantId = matchedBuilding.currentTenantId || matchedBuilding.tenantId;
        }

        // Cross-match in upcoming rent
        if (!tenantId) {
          const upMatch = upcomingItems.find(item => {
            return (item.upcomingRentBuildings || []).some(ub => {
              const ubPhaseNorm = normString(ub.phaseName || ub.phase);
              const ubNumNorm = normString(ub.buildingNumber || ub.number);
              return (
                (ubPhaseNorm.includes(targetPhaseNorm) || targetPhaseNorm.includes(ubPhaseNorm)) &&
                (ubNumNorm === targetNumNorm || ubNumNorm === `house${targetNumNorm}`)
              );
            });
          });
          if (upMatch) {
            matchedTenant = upMatch;
            tenantId = upMatch.tenantId || upMatch.id;
          }
        }

        // Cross-match in all tenants
        if (!tenantId) {
          const tMatch = allTenants.find(t => {
            const blds = t.currentBuildings || t.assignedBuildings || t.buildings || [];
            return blds.some(b => {
              const bPhaseNorm = normString(b.phaseName || b.phase);
              const bNumNorm = normString(b.buildingNumber || b.number);
              return (
                (bPhaseNorm.includes(targetPhaseNorm) || targetPhaseNorm.includes(bPhaseNorm)) &&
                (bNumNorm === targetNumNorm || bNumNorm === `house${targetNumNorm}`)
              );
            });
          });
          if (tMatch) {
            matchedTenant = tMatch;
            tenantId = tMatch.id || tMatch.tenantId;
          }
        }

        if (!matchedTenant && tenantId) {
          matchedTenant = allTenants.find(t => (t.id || t.tenantId) === tenantId) ||
                          upcomingItems.find(t => (t.id || t.tenantId) === tenantId) || null;
        }

        // 3. If tenant assigned, fetch their rent invoices (with components) and payments
        let invoices = [];
        let payments = [];
        let buildingComponents = [];

        const buildingId = matchedBuilding?.id || matchedBuilding?.buildingId;

        const promises = [];
        if (tenantId) {
          promises.push(
            authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/rent-invoices`)
              .then(async res => (res && res.ok ? parseArrayData(safeJsonParse(await res.text())) : []))
              .catch(() => [])
          );
          promises.push(
            authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/payments`)
              .then(async res => (res && res.ok ? parseArrayData(safeJsonParse(await res.text())) : []))
              .catch(() => [])
          );
        } else {
          promises.push(Promise.resolve([]));
          promises.push(Promise.resolve([]));
        }

        if (buildingId) {
          promises.push(
            authFetch(`${API_BASE}/estates/${estateId}/buildings/${buildingId}/invoice-components`)
              .then(async res => {
                if (res && res.ok) {
                  const compData = parseArrayData(safeJsonParse(await res.text()));
                  bCompCache[buildingId] = compData;
                  return compData;
                }
                return [];
              })
              .catch(() => [])
          );
        } else {
          promises.push(Promise.resolve([]));
        }

        const [invData, payData, bCompData] = await Promise.all(promises);
        invoices = invData || [];
        payments = payData || [];
        buildingComponents = bCompData || [];

        checkedCount++;
        setProgressStatus(`Checked ${checkedCount} of ${TARGET_HOUSES.length} target houses...`);

        // Compute amounts and payment status
        const totalPaid = payments.reduce((sum, p) => sum + parseNumericAmount(p.amount || p.amountPaid), 0);
        const totalInvoiced = invoices.reduce((sum, inv) => sum + parseNumericAmount(inv.totalAmount || inv.amountDue), 0);
        const totalRentAmount = invoices.reduce((sum, inv) => sum + parseNumericAmount(inv.rentAmount || 0), 0);
        
        // Extract invoice components across all tenant invoices
        const allInvoiceComponents = [];
        invoices.forEach(inv => {
          (inv.components || []).forEach(c => {
            allInvoiceComponents.push(c);
          });
        });

        const lastPayment = payments.length > 0
          ? payments.slice().sort((a, b) => new Date(b.paidAt || b.createdDate || 0) - new Date(a.paidAt || a.createdDate || 0))[0]
          : null;

        const latestInvoice = invoices.length > 0
          ? invoices.slice().sort((a, b) => new Date(b.dueDate || b.issuedAt || 0) - new Date(a.dueDate || a.issuedAt || 0))[0]
          : null;

        let status = "No Tenant Assigned";
        let statusTone = "secondary";

        if (tenantId) {
          if (payments.length > 0 && totalPaid > 0) {
            if (invoices.length > 0 && totalPaid >= totalInvoiced) {
              status = "Fully Paid";
              statusTone = "success";
            } else if (invoices.length > 0 && totalPaid < totalInvoiced) {
              status = "Partially Paid";
              statusTone = "warning";
            } else {
              status = "Paid";
              statusTone = "success";
            }
          } else if (invoices.length > 0) {
            const hasOverdue = invoices.some(inv => (inv.status || "").toLowerCase() === "overdue");
            if (hasOverdue) {
              status = "Overdue (Unpaid)";
              statusTone = "danger";
            } else {
              status = "Invoiced (Pending Payment)";
              statusTone = "warning";
            }
          } else {
            status = "Tenant Assigned (No Invoices / No Payments)";
            statusTone = "info";
          }
        }

        const tenantFullName = matchedTenant
          ? `${matchedTenant.title ? matchedTenant.title + " " : ""}${matchedTenant.firstName || ""} ${matchedTenant.lastName || ""}`.trim() || matchedTenant.fullName || matchedTenant.name || "Tenant"
          : matchedBuilding?.currentTenantName || matchedBuilding?.tenantName || "—";

        const houseTypeName = matchedBuilding?.typeName || matchedBuilding?.houseType || matchedBuilding?.type || "Standard Unit";

        return {
          targetPhase: target.phase,
          targetNumber: target.number,
          matchedBuilding,
          buildingId,
          houseType: houseTypeName,
          tenantId,
          tenantName: tenantFullName,
          tenantEmail: matchedTenant?.email || "—",
          invoices,
          payments,
          buildingComponents,
          allInvoiceComponents,
          invoicesCount: invoices.length,
          paymentsCount: payments.length,
          totalInvoiced,
          totalRentAmount,
          totalPaid,
          status,
          statusTone,
          lastPaymentDate: lastPayment?.paidAt || lastPayment?.paymentDate || null,
          lastPaymentReference: lastPayment?.gatewayReference || lastPayment?.reference || "—",
          lastPaymentMethod: lastPayment?.paymentMethod || lastPayment?.method || "Bank Transfer",
          latestInvoiceDueDate: latestInvoice?.dueDate || null,
          latestInvoicePeriodStart: latestInvoice?.periodStart || null,
          latestInvoicePeriodEnd: latestInvoice?.periodEnd || null
        };
      });

      setBuildingInvoiceComponentsCache(prev => ({ ...prev, ...bCompCache }));
      setTargetHouseResults(results);
      setProgressStatus(`Completed live check for all ${TARGET_HOUSES.length} target houses.`);
    } catch (err) {
      console.error("Target house check error:", err);
      toast.error("Failed to check target houses against live estate database.");
    } finally {
      setLoading(false);
      setCheckingTargetHouses(false);
    }
  }, [estateId]);

  useEffect(() => {
    checkTargetHouses();
  }, [checkTargetHouses]);

  // Open details modal and fetch fresh building components if needed
  const handleOpenDetailsModal = async (record) => {
    setSelectedRecord(record);
    if (record.buildingId) {
      setLoadingModalComponents(true);
      const comps = await fetchBuildingComponents(record.buildingId);
      setModalBuildingComponents(comps.length > 0 ? comps : (record.buildingComponents || []));
      setLoadingModalComponents(false);
    } else {
      setModalBuildingComponents(record.buildingComponents || []);
    }
  };

  // Extract all invoices from target houses for the invoices tab
  const allInvoicesList = useMemo(() => {
    const list = [];
    targetHouseResults.forEach(h => {
      (h.invoices || []).forEach((inv, idx) => {
        list.push({
          id: inv.id || `inv-${h.targetNumber}-${idx}`,
          houseNumber: h.targetNumber,
          phaseName: h.targetPhase,
          houseType: h.houseType,
          buildingId: inv.buildingId || h.buildingId,
          tenantName: h.tenantName,
          tenantEmail: h.tenantEmail,
          tenantId: h.tenantId,
          rentAmount: parseNumericAmount(inv.rentAmount || 0),
          totalAmount: parseNumericAmount(inv.totalAmount || inv.amountDue || 0),
          dueDate: inv.dueDate,
          periodStart: inv.periodStart,
          periodEnd: inv.periodEnd,
          issuedAt: inv.issuedAt,
          paidAt: inv.paidAt,
          status: inv.status || "Pending",
          components: inv.components || [],
          parentRecord: h
        });
      });
    });
    return list.sort((a, b) => new Date(b.issuedAt || b.dueDate || 0) - new Date(a.issuedAt || a.dueDate || 0));
  }, [targetHouseResults]);

  // Filter computation for target houses table
  const filteredTargetHouses = useMemo(() => {
    let list = [...targetHouseResults];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(h =>
        h.targetNumber.toLowerCase().includes(q) ||
        h.targetPhase.toLowerCase().includes(q) ||
        h.tenantName.toLowerCase().includes(q) ||
        h.tenantEmail.toLowerCase().includes(q) ||
        h.houseType.toLowerCase().includes(q) ||
        h.status.toLowerCase().includes(q)
      );
    }

    if (phaseFilter !== "all") {
      list = list.filter(h => h.targetPhase.toLowerCase() === phaseFilter.toLowerCase());
    }

    if (statusFilter !== "all") {
      list = list.filter(h => {
        const s = h.status.toLowerCase();
        if (statusFilter === "paid") return s.includes("paid") && !s.includes("unpaid");
        if (statusFilter === "unpaid") return s.includes("unpaid") || s.includes("pending") || s.includes("overdue");
        if (statusFilter === "unassigned") return s.includes("no tenant");
        return true;
      });
    }

    return list;
  }, [targetHouseResults, searchQuery, phaseFilter, statusFilter]);

  const totalTablePages = Math.ceil(filteredTargetHouses.length / tablePageSize) || 1;
  const paginatedTargetHouses = useMemo(() => {
    const start = (tablePage - 1) * tablePageSize;
    return filteredTargetHouses.slice(start, start + tablePageSize);
  }, [filteredTargetHouses, tablePage, tablePageSize]);

  // Statistics for the target houses
  const targetStats = useMemo(() => {
    const totalHouses = targetHouseResults.length;
    const paidHouses = targetHouseResults.filter(h => h.statusTone === "success" || h.totalPaid > 0).length;
    const unpaidInvoiced = targetHouseResults.filter(h => h.statusTone === "danger" || h.statusTone === "warning").length;
    const unassigned = targetHouseResults.filter(h => !h.tenantId || h.statusTone === "secondary").length;
    const totalCollected = targetHouseResults.reduce((sum, h) => sum + h.totalPaid, 0);
    const totalInvoicedAmount = targetHouseResults.reduce((sum, h) => sum + h.totalInvoiced, 0);

    return {
      totalHouses,
      paidHouses,
      unpaidInvoiced,
      unassigned,
      totalCollected,
      totalInvoicedAmount
    };
  }, [targetHouseResults]);

  // Export Target Houses to Excel (Cleaned - No Phone Number)
  const handleExportExcel = () => {
    const exportRows = filteredTargetHouses.map((h, idx) => ({
      "S/N": idx + 1,
      "Phase": h.targetPhase,
      "House No": `House ${h.targetNumber}`,
      "House Type": h.houseType,
      "Assigned Tenant": h.tenantName,
      "Tenant Email": h.tenantEmail,
      "Payment Status": h.status,
      "Total Amount Paid (₦)": fmtMoney(h.totalPaid),
      "Total Amount Invoiced (₦)": fmtMoney(h.totalInvoiced),
      "Base Rent (₦)": fmtMoney(h.totalRentAmount),
      "Rental Period": h.latestInvoicePeriodStart && h.latestInvoicePeriodEnd 
        ? `${fmtDate(h.latestInvoicePeriodStart)} - ${fmtDate(h.latestInvoicePeriodEnd)}` 
        : "—",
      "Last Payment Date": fmtDate(h.lastPaymentDate),
      "Payment Reference": h.lastPaymentReference,
      "Payment Method": h.lastPaymentMethod,
      "Invoice Components": (h.allInvoiceComponents || []).map(c => `${c.displayName || c.name || c.code} (${fmtMoney(c.amount)})`).join("; ") || "—"
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payment & Invoice Records");
    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(workbook, `Tenant_Payments_And_Invoices_${dateStr}.xlsx`);
    toast.success("Payment records exported to Excel");
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedRef(true);
    toast.success("Reference copied to clipboard");
    setTimeout(() => setCopiedRef(false), 2000);
  };

  return (
    <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
      <PagePreloader visible={loading} />

      <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
        {/* Mobile Header */}
        <div id="kt_app_header" className="app-header d-flex d-lg-none bg-white border-bottom py-3 px-4 align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-icon btn-sm btn-light" data-bs-toggle="offcanvas" data-bs-target="#kt_app_sidebar" aria-controls="kt_app_sidebar">
              <i className="ki-outline ki-abstract-14 fs-2"></i>
            </button>
            <span className="fs-5 fw-bolder text-gray-900">Paid Tenants & Invoices</span>
          </div>
          <Link to="/dashboard" className="btn btn-sm btn-light-primary">Dashboard</Link>
        </div>

        <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
          <Sidebar collapsible />

          <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
            <div className="d-flex flex-column flex-column-fluid">

              {/* Top Toolbar */}
              <div id="kt_app_toolbar" className="app-toolbar py-4 py-lg-6 border-bottom bg-white">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack flex-wrap gap-4">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h1 className="page-heading d-flex text-gray-900 fw-bold fs-2 flex-column justify-content-center my-0">
                        Paid Tenants, Buildings & Rent Invoices
                      </h1>
                      <span className="badge badge-light-success fs-7 fw-bold">
                        {TARGET_HOUSES.length} Units Checked
                      </span>
                    </div>
                    <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 text-muted">
                      <li className="breadcrumb-item text-muted">
                        <Link to="/dashboard" className="text-muted text-hover-primary">Home</Link>
                      </li>
                      <li className="breadcrumb-item"><span className="bullet bg-gray-300 w-5px h-2px"></span></li>
                      <li className="breadcrumb-item text-muted">
                        <Link to="/rent-management" className="text-muted text-hover-primary">Rent Management</Link>
                      </li>
                      <li className="breadcrumb-item"><span className="bullet bg-gray-300 w-5px h-2px"></span></li>
                      <li className="breadcrumb-item text-gray-900">Payment & Invoice Information</li>
                    </ul>
                  </div>

                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-sm btn-light d-flex align-items-center gap-2"
                      onClick={checkTargetHouses}
                      disabled={checkingTargetHouses}
                    >
                      <RefreshCw className={`w-4 h-4 ${checkingTargetHouses ? "animate-spin text-primary" : ""}`} />
                      <span>{checkingTargetHouses ? "Refreshing Data..." : "Refresh Records"}</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light-success d-flex align-items-center gap-2"
                      onClick={handleExportExcel}
                    >
                      <Download className="w-4 h-4" />
                      <span>Export Excel</span>
                    </button>

                    <Link to="/rent-management" className="btn btn-sm btn-success d-flex align-items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      <span>Rent Management</span>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Content Body */}
              <div id="kt_app_content" className="app-content flex-column-fluid py-6">
                <div id="kt_app_content_container" className="app-container container-xxl">

                  {/* Status Banner */}
                  <div className="card border-0 mb-6 bg-light-success border border-success border-opacity-25 rounded-3 shadow-none">
                    <div className="card-body p-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="symbol symbol-40px symbol-circle bg-success text-white d-flex align-items-center justify-content-center fw-bold fs-6">
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="fs-6 fw-bold text-gray-900">
                            Live Payment Reconciliation & Rent Invoices with Component Breakdown
                          </div>
                          <div className="fs-7 text-muted">
                            {progressStatus || "Loaded tenant payments, building details, rent invoices and invoice components catalog."}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-white text-success border border-success fs-7 fw-bold px-3 py-2">
                          {targetStats.paidHouses} Paid / {targetStats.totalHouses} Houses Checked
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* KPI Statistics */}
                  <div className="row g-5 g-xl-6 mb-6">
                    {/* Paid Houses */}
                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="card card-flush h-100 border border-gray-200 shadow-sm bg-body">
                        <div className="card-body p-6">
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <div className="symbol symbol-45px symbol-circle bg-light-success">
                              <span className="symbol-label">
                                <CheckCircle className="w-5 h-5 text-success" />
                              </span>
                            </div>
                            <span className="badge badge-light-success fw-bold fs-8">Paid</span>
                          </div>
                          <div className="text-gray-500 fw-semibold fs-7 mb-1">Tenants Paid</div>
                          <div className="fs-2hx fw-bold text-gray-900 mb-1">
                            {targetStats.paidHouses}
                          </div>
                          <div className="text-muted fs-8">
                            Out of {targetStats.totalHouses} monitored units
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Total Collected */}
                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="card card-flush h-100 border border-gray-200 shadow-sm bg-body">
                        <div className="card-body p-6">
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <div className="symbol symbol-45px symbol-circle bg-light-primary">
                              <span className="symbol-label">
                                <Wallet className="w-5 h-5 text-primary" />
                              </span>
                            </div>
                            <span className="badge badge-light-primary fw-bold fs-8">Revenue</span>
                          </div>
                          <div className="text-gray-500 fw-semibold fs-7 mb-1">Total Rent Collected</div>
                          <div className="fs-2hx fw-bold text-gray-900 mb-1">
                            {fmtMoney(targetStats.totalCollected)}
                          </div>
                          <div className="text-muted fs-8">
                            Confirmed live payment settlements
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Unpaid / Pending */}
                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="card card-flush h-100 border border-gray-200 shadow-sm bg-body">
                        <div className="card-body p-6">
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <div className="symbol symbol-45px symbol-circle bg-light-warning">
                              <span className="symbol-label">
                                <Clock className="w-5 h-5 text-warning" />
                              </span>
                            </div>
                            <span className="badge badge-light-warning fw-bold fs-8">Pending</span>
                          </div>
                          <div className="text-gray-500 fw-semibold fs-7 mb-1">Unpaid / Invoiced</div>
                          <div className="fs-2hx fw-bold text-gray-900 mb-1">
                            {targetStats.unpaidInvoiced}
                          </div>
                          <div className="text-muted fs-8">
                            Awaiting payment settlement
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Invoice Components Configured */}
                    {!isPhaseAdmin() && (
                    <>
                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="card card-flush h-100 border border-gray-200 shadow-sm bg-body">
                        <div className="card-body p-6">
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <div className="symbol symbol-45px symbol-circle bg-light-info">
                              <span className="symbol-label">
                                <Layers className="w-5 h-5 text-info" />
                              </span>
                            </div>
                            <span className="badge badge-light-info fw-bold fs-8">Catalog</span>
                          </div>
                          <div className="text-gray-500 fw-semibold fs-7 mb-1">Estate Components</div>
                          <div className="fs-2hx fw-bold text-gray-900 mb-1">
                            {estateInvoiceComponents.length || "Active"}
                          </div>
                          <div className="text-muted fs-8">
                            Standard billing definitions
                          </div>
                        </div>
                      </div>
                    </div>
                    </>
                    )}
                  </div>

                  {/* Navigation Tabs */}
                  <div className="d-flex align-items-center gap-2 mb-6 border-bottom border-gray-200 pb-3">
                    <button
                      className={`btn btn-sm ${activeTab === "targetHouses" ? "btn-primary" : "btn-light"}`}
                      onClick={() => setActiveTab("targetHouses")}
                    >
                      <Building2 className="w-4 h-4 me-1.5" />
                      Paid Tenants & Target Houses ({targetHouseResults.length})
                    </button>

                    <button
                      className={`btn btn-sm ${activeTab === "allInvoices" ? "btn-primary" : "btn-light"}`}
                      onClick={() => setActiveTab("allInvoices")}
                    >
                      <Receipt className="w-4 h-4 me-1.5" />
                      Rent Invoices & Components Breakdown ({allInvoicesList.length})
                    </button>

                    {!isPhaseAdmin() && (
                      <button
                        className={`btn btn-sm ${activeTab === "estateComponents" ? "btn-primary" : "btn-light"}`}
                        onClick={() => setActiveTab("estateComponents")}
                      >
                        <Layers className="w-4 h-4 me-1.5" />
                        Estate Invoice Component Definitions ({estateInvoiceComponents.length})
                      </button>
                    )}
                  </div>

                  {/* TAB 1: TARGET HOUSES & PAID TENANTS */}
                  {activeTab === "targetHouses" && (
                    <>
                      {/* Filter & Search Bar */}
                      <div className="card border border-gray-200 shadow-sm mb-6">
                        <div className="card-body p-5">
                          <div className="row g-4 align-items-center">
                            {/* Search */}
                            <div className="col-12 col-md-4">
                              <div className="position-relative">
                                <Search className="w-4 h-4 text-gray-500 position-absolute top-50 start-0 translate-middle-y ms-4" />
                                <input
                                  type="text"
                                  className="form-control form-control-solid ps-12 fs-7"
                                  placeholder="Search by tenant name, building no, phase, or house type..."
                                  value={searchQuery}
                                  onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setTablePage(1);
                                  }}
                                />
                                {searchQuery && (
                                  <button
                                    className="btn btn-icon btn-sm btn-light position-absolute top-50 end-0 translate-middle-y me-2"
                                    onClick={() => setSearchQuery("")}
                                  >
                                    <XCircle className="w-4 h-4 text-gray-400" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Phase Filter */}
                            <div className="col-6 col-md-3">
                              <select
                                className="form-select form-select-solid fs-7"
                                value={phaseFilter}
                                onChange={(e) => {
                                  setPhaseFilter(e.target.value);
                                  setTablePage(1);
                                }}
                              >
                                <option value="all">All Phases</option>
                                <option value="Phase 4">Phase 4 (22 Houses)</option>
                                <option value="Phase 2">Phase 2 (6 Houses)</option>
                                <option value="Phase 3">Phase 3 (1 House)</option>
                              </select>
                            </div>

                            {/* Status Filter */}
                            <div className="col-6 col-md-3">
                              <select
                                className="form-select form-select-solid fs-7"
                                value={statusFilter}
                                onChange={(e) => {
                                  setStatusFilter(e.target.value);
                                  setTablePage(1);
                                }}
                              >
                                <option value="all">All Payment Statuses</option>
                                <option value="paid">Paid Units</option>
                                <option value="unpaid">Unpaid / Invoiced</option>
                                <option value="unassigned">Unassigned / No Tenant</option>
                              </select>
                            </div>

                            <div className="col-12 col-md-2 text-end">
                              <span className="text-muted fs-7">
                                Showing <strong>{filteredTargetHouses.length}</strong> of <strong>{TARGET_HOUSES.length}</strong>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Main Table */}
                      <div className="card border border-gray-200 shadow-sm">
                        <div className="card-header border-0 pt-6">
                          <div className="card-title">
                            <div className="d-flex align-items-center gap-2">
                              <Building2 className="w-5 h-5 text-success" />
                              <h3 className="fw-bold text-gray-900 fs-4 mb-0">
                                Target Units Payment & Tenancy Directory ({filteredTargetHouses.length})
                              </h3>
                            </div>
                          </div>

                          <div className="card-toolbar">
                            <span className="text-muted fs-7">
                              Click "View Information" to inspect all components & invoice records
                            </span>
                          </div>
                        </div>

                        <div className="card-body pt-0">
                          {checkingTargetHouses ? (
                            <div className="d-flex flex-column align-items-center justify-content-center py-20 text-muted">
                              <div className="spinner-border text-success mb-3" role="status"></div>
                              <span className="fs-6 fw-semibold">Checking payments and invoice components...</span>
                              <span className="fs-8 text-gray-500 mt-1">{progressStatus}</span>
                            </div>
                          ) : paginatedTargetHouses.length === 0 ? (
                            <div className="text-center py-15">
                              <div className="symbol symbol-70px symbol-circle bg-light-warning mx-auto mb-4 d-flex align-items-center justify-content-center">
                                <AlertCircle className="w-8 h-8 text-warning" />
                              </div>
                              <h4 className="fw-bold text-gray-900 mb-1">No Records Found</h4>
                              <p className="text-muted fs-7 mb-4">
                                Try adjusting your search query or phase filter.
                              </p>
                              <button
                                className="btn btn-sm btn-light-primary"
                                onClick={() => {
                                  setSearchQuery("");
                                  setPhaseFilter("all");
                                  setStatusFilter("all");
                                }}
                              >
                                Reset Filters
                              </button>
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table align-middle table-row-dashed fs-6 gy-4">
                                <thead>
                                  <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0 border-bottom border-gray-200 bg-light-secondary">
                                    <th className="ps-4 min-w-140px">Building / Unit</th>
                                    <th className="min-w-180px">Tenant Name & Email</th>
                                    <th className="min-w-120px text-center">Payment Status</th>
                                    <th className="min-w-130px">Amount Paid</th>
                                    <th className="min-w-140px">Total Invoiced / Rent</th>
                                    <th className="min-w-130px">Period / Due Date</th>
                                    <th className="min-w-140px">Components Breakdown</th>
                                    <th className="min-w-100px text-end pe-4">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="text-gray-700 fw-semibold">
                                  {paginatedTargetHouses.map((h) => {
                                    const isPaid = h.statusTone === "success" || h.totalPaid > 0;

                                    return (
                                      <tr key={`${h.targetPhase}-${h.targetNumber}`} className="hover-table-row">
                                        {/* Building & Phase */}
                                        <td className="ps-4">
                                          <div className="d-flex flex-column">
                                            <div className="d-flex align-items-center gap-2">
                                              <span className="fw-bolder fs-6 text-gray-900">
                                                House {h.targetNumber}
                                              </span>
                                              <span className="badge badge-light-dark fw-bold fs-8">
                                                {h.targetPhase}
                                              </span>
                                            </div>
                                            <span className="text-muted fs-8 mt-0.5">
                                              {h.houseType}
                                            </span>
                                          </div>
                                        </td>

                                        {/* Tenant Name & Email (No Phone Number) */}
                                        <td>
                                          {h.tenantId ? (
                                            <div className="d-flex align-items-center">
                                              <div className="symbol symbol-35px symbol-circle me-3 overflow-hidden">
                                                <div className="symbol-label fs-8 fw-bold bg-light-success text-success">
                                                  {getInitials(h.tenantName)}
                                                </div>
                                              </div>
                                              <div className="d-flex flex-column">
                                                <span className="text-gray-900 fw-bold fs-6">
                                                  {h.tenantName}
                                                </span>
                                                <span className="text-muted fs-8">{h.tenantEmail}</span>
                                              </div>
                                            </div>
                                          ) : (
                                            <span className="text-muted fs-7 fst-italic">
                                              {h.tenantName !== "—" ? h.tenantName : "No active tenant assigned"}
                                            </span>
                                          )}
                                        </td>

                                        {/* Payment Status Badge */}
                                        <td className="text-center">
                                          <span className={`badge badge-light-${h.statusTone} text-${h.statusTone} fw-bold fs-8 px-3 py-2`}>
                                            {h.status}
                                          </span>
                                        </td>

                                        {/* Amount Paid */}
                                        <td>
                                          <div className="d-flex flex-column">
                                            <span className={`fw-bolder fs-6 ${isPaid ? "text-success" : "text-gray-500"}`}>
                                              {fmtMoney(h.totalPaid)}
                                            </span>
                                            {h.lastPaymentDate && (
                                              <span className="text-muted fs-8">
                                                Paid: {fmtDate(h.lastPaymentDate)}
                                              </span>
                                            )}
                                          </div>
                                        </td>

                                        {/* Total Invoiced & Base Rent */}
                                        <td>
                                          <div className="d-flex flex-column">
                                            <span className="fw-semibold text-gray-900 fs-6">
                                              {h.totalInvoiced > 0 ? fmtMoney(h.totalInvoiced) : "—"}
                                            </span>
                                            {h.totalRentAmount > 0 && h.totalRentAmount !== h.totalInvoiced && (
                                              <span className="text-muted fs-8">
                                                Base: {fmtMoney(h.totalRentAmount)}
                                              </span>
                                            )}
                                          </div>
                                        </td>

                                        {/* Period / Due Date */}
                                        <td>
                                          <div className="d-flex flex-column">
                                            {h.latestInvoicePeriodStart && h.latestInvoicePeriodEnd ? (
                                              <span className="text-gray-800 fs-7">
                                                {fmtDate(h.latestInvoicePeriodStart)} – {fmtDate(h.latestInvoicePeriodEnd)}
                                              </span>
                                            ) : h.latestInvoiceDueDate ? (
                                              <span className="text-gray-800 fs-7">
                                                Due: {fmtDate(h.latestInvoiceDueDate)}
                                              </span>
                                            ) : (
                                              <span className="text-muted fs-8">—</span>
                                            )}
                                          </div>
                                        </td>

                                        {/* Components Breakdown Badge */}
                                        <td>
                                          {h.allInvoiceComponents.length > 0 ? (
                                            <div className="d-flex flex-wrap gap-1">
                                              {h.allInvoiceComponents.slice(0, 2).map((c, cIdx) => (
                                                <span key={cIdx} className="badge badge-light-primary fs-8">
                                                  {c.displayName || c.name || c.code}: {fmtMoney(c.amount)}
                                                </span>
                                              ))}
                                              {h.allInvoiceComponents.length > 2 && (
                                                <span className="badge badge-light fs-8 text-muted">
                                                  +{h.allInvoiceComponents.length - 2} more
                                                </span>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-muted fs-8">Standard Rent</span>
                                          )}
                                        </td>

                                        {/* Action Button */}
                                        <td className="text-end pe-4">
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-light-primary d-inline-flex align-items-center gap-1.5"
                                            onClick={() => handleOpenDetailsModal(h)}
                                          >
                                            <Eye className="w-3.5 h-3.5" />
                                            <span>View Details</span>
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Pagination Footer */}
                          {filteredTargetHouses.length > tablePageSize && (
                            <div className="d-flex flex-stack flex-wrap p-6 border-top border-gray-200">
                              <div className="fs-7 fw-semibold text-gray-700">
                                Showing {((tablePage - 1) * tablePageSize) + 1} to {Math.min(tablePage * tablePageSize, filteredTargetHouses.length)} of {filteredTargetHouses.length} units
                              </div>

                              <ul className="pagination pagination-circle pagination-outline mb-0">
                                <li className={`page-item previous ${tablePage === 1 ? "disabled" : ""}`}>
                                  <button className="page-link" onClick={() => setTablePage(p => Math.max(1, p - 1))}>
                                    <ChevronLeft className="w-4 h-4" />
                                  </button>
                                </li>

                                {Array.from({ length: totalTablePages }, (_, i) => i + 1).map((pg) => (
                                  <li key={pg} className={`page-item ${tablePage === pg ? "active" : ""}`}>
                                    <button className="page-link" onClick={() => setTablePage(pg)}>{pg}</button>
                                  </li>
                                ))}

                                <li className={`page-item next ${tablePage === totalTablePages ? "disabled" : ""}`}>
                                  <button className="page-link" onClick={() => setTablePage(p => Math.min(totalTablePages, p + 1))}>
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* TAB 2: RENT INVOICES & COMPONENT BREAKDOWN */}
                  {activeTab === "allInvoices" && (
                    <div className="card border border-gray-200 shadow-sm">
                      <div className="card-header border-0 pt-6">
                        <div className="card-title">
                          <div className="d-flex align-items-center gap-2">
                            <Receipt className="w-5 h-5 text-primary" />
                            <h3 className="fw-bold text-gray-900 fs-4 mb-0">
                              Rent Invoices with Itemized Components ({allInvoicesList.length})
                            </h3>
                          </div>
                        </div>
                        <div className="card-toolbar">
                          <span className="text-muted fs-7">
                            Direct from Rent Invoices API (/estates/{estateId}/tenants/{`{tenantId}`}/rent-invoices)
                          </span>
                        </div>
                      </div>

                      <div className="card-body pt-0">
                        {allInvoicesList.length === 0 ? (
                          <div className="text-center py-12 text-muted">
                            <FileText className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                            <h5 className="text-gray-800 fw-bold">No Invoices Found</h5>
                            <p className="fs-7 text-muted">No rent invoice items generated yet for the monitored houses.</p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table align-middle table-row-dashed fs-6 gy-4">
                              <thead>
                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0 border-bottom border-gray-200 bg-light-secondary">
                                  <th className="ps-4 min-w-120px">Invoice No / ID</th>
                                  <th className="min-w-140px">Building & Unit</th>
                                  <th className="min-w-160px">Tenant Name</th>
                                  <th className="min-w-120px">Status</th>
                                  <th className="min-w-120px">Base Rent</th>
                                  <th className="min-w-120px">Total Invoiced</th>
                                  <th className="min-w-140px">Period / Due Date</th>
                                  <th className="min-w-160px">Components Included</th>
                                  <th className="min-w-80px text-end pe-4">Action</th>
                                </tr>
                              </thead>
                              <tbody className="text-gray-700 fw-semibold">
                                {allInvoicesList.map((inv, idx) => (
                                  <tr key={inv.id || idx}>
                                    <td className="ps-4">
                                      <span className="fw-bold text-gray-900 fs-7 font-monospace">
                                        {inv.id ? inv.id.slice(0, 12) + "..." : `INV-${idx + 1}`}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="d-flex flex-column">
                                        <span className="fw-bolder text-gray-900 fs-6">
                                          House {inv.houseNumber} ({inv.phaseName})
                                        </span>
                                        <span className="text-muted fs-8">{inv.houseType}</span>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="d-flex flex-column">
                                        <span className="text-gray-900 fw-bold fs-6">{inv.tenantName}</span>
                                        <span className="text-muted fs-8">{inv.tenantEmail}</span>
                                      </div>
                                    </td>
                                    <td>
                                      <span className={`badge badge-light-${(inv.status || "").toLowerCase().includes("paid") ? "success" : "warning"} fw-bold fs-8`}>
                                        {inv.status}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fw-bold text-gray-800 fs-6">{fmtMoney(inv.rentAmount)}</span>
                                    </td>
                                    <td>
                                      <span className="fw-bolder text-gray-900 fs-6">{fmtMoney(inv.totalAmount)}</span>
                                    </td>
                                    <td>
                                      <div className="d-flex flex-column">
                                        <span className="text-gray-800 fs-7">
                                          {inv.periodStart && inv.periodEnd
                                            ? `${fmtDate(inv.periodStart)} – ${fmtDate(inv.periodEnd)}`
                                            : `Due: ${fmtDate(inv.dueDate)}`}
                                        </span>
                                      </div>
                                    </td>
                                    <td>
                                      {inv.components && inv.components.length > 0 ? (
                                        <div className="d-flex flex-column gap-1">
                                          {inv.components.map((c, cIdx) => (
                                            <span key={cIdx} className="badge badge-light-primary fs-8 text-start">
                                              {c.displayName || c.name || c.code}: {fmtMoney(c.amount)}
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="text-muted fs-8">Base Rent Only</span>
                                      )}
                                    </td>
                                    <td className="text-end pe-4">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-light-primary"
                                        onClick={() => handleOpenDetailsModal(inv.parentRecord)}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* TAB 3: ESTATE INVOICE COMPONENT DEFINITIONS */}
                  {activeTab === "estateComponents" && !isPhaseAdmin() && (
                    <div className="card border border-gray-200 shadow-sm">
                      <div className="card-header border-0 pt-6">
                        <div className="card-title">
                          <div className="d-flex align-items-center gap-2">
                            <Layers className="w-5 h-5 text-info" />
                            <h3 className="fw-bold text-gray-900 fs-4 mb-0">
                              Estate Invoice Component Definitions Catalog ({estateInvoiceComponents.length})
                            </h3>
                          </div>
                        </div>
                        <div className="card-toolbar">
                          <span className="text-muted fs-7">
                            Live catalog from /estates/{estateId}/invoice-components
                          </span>
                        </div>
                      </div>

                      <div className="card-body pt-0">
                        {estateInvoiceComponents.length === 0 ? (
                          <div className="text-center py-12 text-muted">
                            <Layers className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                            <h5 className="text-gray-800 fw-bold">No Estate Components Configured</h5>
                            <p className="fs-7 text-muted">Estate-level invoice component definitions have not been populated yet.</p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table align-middle table-row-dashed fs-6 gy-4">
                              <thead>
                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0 border-bottom border-gray-200 bg-light-secondary">
                                  <th className="ps-4 min-w-180px">Display Name</th>
                                  <th className="min-w-120px">Component Code</th>
                                  <th className="min-w-200px">Description</th>
                                  <th className="min-w-100px text-center">System Component</th>
                                  <th className="min-w-100px text-center">Active Status</th>
                                  <th className="min-w-80px text-center">Sort Order</th>
                                  <th className="min-w-60px text-end pe-4">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="text-gray-700 fw-semibold">
                                {estateInvoiceComponents.map((comp, idx) => (
                                  <tr key={comp.id || idx}>
                                    <td className="ps-4">
                                      <div className="d-flex align-items-center gap-2">
                                        <Tag className="w-4 h-4 text-info" />
                                        <span className="fw-bold text-gray-900 fs-6">
                                          {comp.displayName || comp.defaultDisplayName || "—"}
                                        </span>
                                      </div>
                                    </td>
                                    <td>
                                      <span className="badge badge-light-dark font-monospace fs-7">
                                        {comp.componentCode || comp.codeText || comp.code || comp.codeName || "—"}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="text-gray-600 fs-7">
                                        {comp.description || "Estate billing line item component"}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      {comp.isSystem ? (
                                        <span className="badge badge-light-primary fs-8">System</span>
                                      ) : (
                                        <span className="badge badge-light-secondary fs-8">Custom</span>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {comp.isActive !== false ? (
                                        <span className="badge badge-light-success fs-8">Active</span>
                                      ) : (
                                        <span className="badge badge-light-danger fs-8">Inactive</span>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      <span className="text-muted fs-7">{comp.sortOrder ?? idx}</span>
                                    </td>
                                    <td className="text-end pe-4">
                                      {comp.isActive !== false && (
                                        <button
                                          type="button"
                                          className="btn btn-icon btn-sm btn-light-danger"
                                          title="Remove component"
                                          onClick={() => handleDeleteEstateComponent(comp)}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* MODAL: Comprehensive Information for Person Who Paid & Building / Invoice Components */}
              {selectedRecord && (
                <div
                  className="modal fade show d-block"
                  tabIndex="-1"
                  style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: 1060 }}
                >
                  <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
                    <div className="modal-content shadow-lg border-0 rounded-4">
                      {/* Modal Header */}
                      <div className="modal-header border-bottom py-4 px-6 bg-light d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                          <div className="symbol symbol-45px symbol-circle bg-light-success text-success d-flex align-items-center justify-content-center fw-bold fs-5">
                            {getInitials(selectedRecord.tenantName)}
                          </div>
                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <h4 className="modal-title fw-bold text-gray-900 mb-0">
                                {selectedRecord.tenantName}
                              </h4>
                              <span className={`badge badge-light-${selectedRecord.statusTone} fw-bold fs-8`}>
                                {selectedRecord.status}
                              </span>
                            </div>
                            <span className="text-muted fs-7">
                              House {selectedRecord.targetNumber} • {selectedRecord.targetPhase} • {selectedRecord.houseType}
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm btn-icon btn-light"
                          onClick={() => setSelectedRecord(null)}
                        >
                          <X className="w-5 h-5 text-gray-700" />
                        </button>
                      </div>

                      {/* Modal Body */}
                      <div className="modal-body p-6">
                        {/* Section 1: Tenant & Property Information (No Phone Number) */}
                        <div className="row g-4 mb-6">
                          <div className="col-12 col-md-6">
                            <div className="card h-100 bg-light border border-gray-200">
                              <div className="card-body p-5">
                                <h6 className="fw-bold text-gray-900 mb-4 d-flex align-items-center gap-2">
                                  <User className="w-4 h-4 text-primary" />
                                  <span>Tenant Information</span>
                                </h6>
                                <div className="d-flex flex-column gap-2.5 fs-7">
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Full Name:</span>
                                    <span className="fw-bold text-gray-900">{selectedRecord.tenantName}</span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Email Address:</span>
                                    <span className="fw-semibold text-gray-800">{selectedRecord.tenantEmail}</span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Tenant ID:</span>
                                    <span className="font-monospace text-gray-700 fs-8">{selectedRecord.tenantId || "—"}</span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Payment Reconciliation:</span>
                                    <span className={`badge badge-light-${selectedRecord.statusTone} fs-8`}>
                                      {selectedRecord.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-md-6">
                            <div className="card h-100 bg-light border border-gray-200">
                              <div className="card-body p-5">
                                <h6 className="fw-bold text-gray-900 mb-4 d-flex align-items-center gap-2">
                                  <Home className="w-4 h-4 text-success" />
                                  <span>Building & Property Details</span>
                                </h6>
                                <div className="d-flex flex-column gap-2.5 fs-7">
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Building / House:</span>
                                    <span className="fw-bold text-gray-900">House {selectedRecord.targetNumber}</span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Estate Phase:</span>
                                    <span className="fw-bold text-gray-900">{selectedRecord.targetPhase}</span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">House Model / Type:</span>
                                    <span className="fw-semibold text-gray-800">{selectedRecord.houseType}</span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Building ID:</span>
                                    <span className="font-monospace text-gray-700 fs-8">{selectedRecord.buildingId || "—"}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Financial Overview Summary */}
                        <div className="row g-4 mb-6">
                          <div className="col-12 col-sm-4">
                            <div className="p-4 rounded-3 bg-light-success border border-success border-opacity-25 text-center">
                              <span className="fs-8 fw-semibold text-success d-block mb-1">TOTAL AMOUNT PAID</span>
                              <span className="fs-2 fw-bolder text-success">{fmtMoney(selectedRecord.totalPaid)}</span>
                            </div>
                          </div>

                          <div className="col-12 col-sm-4">
                            <div className="p-4 rounded-3 bg-light-primary border border-primary border-opacity-25 text-center">
                              <span className="fs-8 fw-semibold text-primary d-block mb-1">TOTAL AMOUNT INVOICED</span>
                              <span className="fs-2 fw-bolder text-primary">{fmtMoney(selectedRecord.totalInvoiced)}</span>
                            </div>
                          </div>

                          <div className="col-12 col-sm-4">
                            <div className="p-4 rounded-3 bg-light-info border border-info border-opacity-25 text-center">
                              <span className="fs-8 fw-semibold text-info d-block mb-1">BASE RENT AMOUNT</span>
                              <span className="fs-2 fw-bolder text-info">{fmtMoney(selectedRecord.totalRentAmount)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Section 2: Rent Invoices & Invoice Line Components */}
                        <div className="card border border-gray-200 mb-6">
                          <div className="card-header border-0 py-4 px-6 bg-light d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                              <Receipt className="w-5 h-5 text-primary" />
                              <h5 className="fw-bold text-gray-900 mb-0">
                                Rent Invoices & Line Components ({selectedRecord.invoices?.length || 0})
                              </h5>
                            </div>
                            <span className="text-muted fs-8">
                              GET /estates/{estateId}/tenants/{selectedRecord.tenantId}/rent-invoices
                            </span>
                          </div>

                          <div className="card-body p-5">
                            {(!selectedRecord.invoices || selectedRecord.invoices.length === 0) ? (
                              <div className="text-center py-6 text-muted">
                                <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                <span className="fs-7">No rent invoices recorded for this tenant.</span>
                              </div>
                            ) : (
                              <div className="d-flex flex-column gap-4">
                                {selectedRecord.invoices.map((inv, idx) => (
                                  <div key={inv.id || idx} className="p-4 border rounded-3 bg-body">
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 pb-3 border-bottom">
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="badge badge-light-dark font-monospace fs-7">
                                          {inv.id ? `INV-${inv.id.slice(0, 8)}` : `Invoice #${idx + 1}`}
                                        </span>
                                        <span className={`badge badge-light-${(inv.status || "").toLowerCase().includes("paid") ? "success" : "warning"} fs-8`}>
                                          {inv.status || "Pending"}
                                        </span>
                                      </div>

                                      <div className="d-flex align-items-center gap-3 fs-7">
                                        {inv.periodStart && inv.periodEnd && (
                                          <span className="text-muted">
                                            Period: <strong className="text-gray-800">{fmtDate(inv.periodStart)} – {fmtDate(inv.periodEnd)}</strong>
                                          </span>
                                        )}
                                        {inv.dueDate && (
                                          <span className="text-muted">
                                            Due: <strong className="text-gray-800">{fmtDate(inv.dueDate)}</strong>
                                          </span>
                                        )}
                                        {inv.paidAt && (
                                          <span className="badge badge-light-success fs-8">
                                            Paid on {fmtDate(inv.paidAt)}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Amounts Summary */}
                                    <div className="row g-3 mb-3">
                                      <div className="col-6 col-md-4">
                                        <div className="p-2.5 bg-light rounded-2">
                                          <span className="fs-8 text-muted d-block">Base Rent</span>
                                          <span className="fs-6 fw-bold text-gray-900">{fmtMoney(inv.rentAmount)}</span>
                                        </div>
                                      </div>
                                      <div className="col-6 col-md-4">
                                        <div className="p-2.5 bg-light rounded-2">
                                          <span className="fs-8 text-muted d-block">Total Invoice Amount</span>
                                          <span className="fs-6 fw-bolder text-primary">{fmtMoney(inv.totalAmount)}</span>
                                        </div>
                                      </div>
                                      <div className="col-12 col-md-4">
                                        <div className="p-2.5 bg-light rounded-2">
                                          <span className="fs-8 text-muted d-block">Issued At</span>
                                          <span className="fs-6 fw-semibold text-gray-800">{fmtDateTime(inv.issuedAt)}</span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Components Table inside Invoice */}
                                    <div>
                                      <h6 className="fs-8 text-uppercase fw-bold text-muted mb-2">Itemized Invoice Components:</h6>
                                      {inv.components && inv.components.length > 0 ? (
                                        <div className="table-responsive">
                                          <table className="table table-sm table-bordered align-middle fs-7 mb-0">
                                            <thead className="bg-light">
                                              <tr>
                                                <th className="ps-3 py-2">Component Name / Display</th>
                                                <th className="py-2">Code</th>
                                                <th className="py-2">Type</th>
                                                <th className="pe-3 py-2 text-end">Amount</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {inv.components.map((comp, cIdx) => (
                                                <tr key={comp.assignmentId || cIdx}>
                                                  <td className="ps-3 py-2 fw-semibold text-gray-900">
                                                    {comp.displayName || comp.name || "—"}
                                                  </td>
                                                  <td className="py-2 font-monospace fs-8 text-muted">
                                                    {comp.componentCode || comp.codeText || comp.code || comp.codeName || "—"}
                                                  </td>
                                                  <td className="py-2">
                                                    <span className="badge badge-light fs-8">{comp.componentType || comp.typeText || comp.type || "Component"}</span>
                                                  </td>
                                                  <td className="pe-3 py-2 text-end fw-bold text-gray-900">
                                                    {fmtMoney(comp.amount)}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <span className="text-muted fs-8 fst-italic">No additional components attached to this invoice.</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Section 3: Building Configured Invoice Components */}
                        <div className="card border border-gray-200 mb-6">
                          <div className="card-header border-0 py-4 px-6 bg-light d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                              <Building2 className="w-5 h-5 text-success" />
                              <h5 className="fw-bold text-gray-900 mb-0">
                                Building Invoice Components ({modalBuildingComponents.length})
                              </h5>
                            </div>
                            <span className="text-muted fs-8">
                              GET /estates/{estateId}/buildings/{selectedRecord.buildingId}/invoice-components
                            </span>
                          </div>

                          <div className="card-body p-5">
                            {loadingModalComponents ? (
                              <div className="text-center py-4 text-muted">
                                <div className="spinner-border spinner-border-sm text-success me-2" role="status"></div>
                                <span className="fs-7">Loading building invoice components...</span>
                              </div>
                            ) : modalBuildingComponents.length === 0 ? (
                              <div className="text-center py-4 text-muted">
                                <span className="fs-7">No specific invoice components assigned to this building.</span>
                              </div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table table-sm align-middle table-row-dashed fs-7 mb-0">
                                  <thead>
                                    <tr className="text-start text-muted fw-bold fs-8 text-uppercase gs-0 bg-light">
                                      <th className="ps-3 py-2">Component Name</th>
                                      <th className="py-2">Code</th>
                                      <th className="py-2">Configured Amount</th>
                                      <th className="py-2">Effective Period</th>
                                      <th className="py-2 text-end">Active</th>
                                      <th className="pe-3 py-2 text-end"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {modalBuildingComponents.map((bComp, idx) => (
                                      <tr key={bComp.id || idx}>
                                        <td className="ps-3 py-2 fw-semibold text-gray-900">
                                          {bComp.displayName || bComp.defaultDisplayName || "—"}
                                        </td>
                                        <td className="py-2 font-monospace fs-8 text-muted">
                                          {bComp.code || bComp.codeName || "—"}
                                        </td>
                                        <td className="py-2 fw-bold text-gray-900">
                                          {fmtMoney(bComp.amount)}
                                        </td>
                                        <td className="py-2 text-muted fs-8">
                                          {bComp.effectiveFrom ? `${fmtDate(bComp.effectiveFrom)} – ${fmtDate(bComp.effectiveTo)}` : "Ongoing"}
                                        </td>
                                        <td className="py-2 text-end">
                                          {bComp.isActive !== false ? (
                                            <span className="badge badge-light-success fs-8">Active</span>
                                          ) : (
                                            <span className="badge badge-light-danger fs-8">Inactive</span>
                                          )}
                                        </td>
                                        <td className="pe-3 py-2 text-end">
                                          {bComp.isActive !== false && (
                                            <button
                                              type="button"
                                              className="btn btn-icon btn-sm btn-light-danger"
                                              title="Remove from building"
                                              onClick={() => handleDeleteBuildingComponent(selectedRecord.buildingId, bComp)}
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Section 4: Payment Transactions & Receipt History */}
                        <div className="card border border-gray-200">
                          <div className="card-header border-0 py-4 px-6 bg-light d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                              <Wallet className="w-5 h-5 text-success" />
                              <h5 className="fw-bold text-gray-900 mb-0">
                                Payment Settlements & Transactions ({selectedRecord.payments?.length || 0})
                              </h5>
                            </div>
                            <span className="text-muted fs-8">
                              Confirmed live tenant payments
                            </span>
                          </div>

                          <div className="card-body p-5">
                            {(!selectedRecord.payments || selectedRecord.payments.length === 0) ? (
                              <div className="text-center py-6 text-muted">
                                <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                <span className="fs-7">No payment transactions recorded for this unit yet.</span>
                              </div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table align-middle table-row-dashed fs-7 gy-3 mb-0">
                                  <thead>
                                    <tr className="text-start text-muted fw-bold fs-8 text-uppercase gs-0 bg-light">
                                      <th className="ps-3 py-2">Paid Date & Time</th>
                                      <th className="py-2">Amount Paid</th>
                                      <th className="py-2">Payment Method</th>
                                      <th className="py-2">Gateway Reference</th>
                                      <th className="pe-3 py-2 text-end">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedRecord.payments.map((p, pIdx) => {
                                      const ref = p.gatewayReference || p.reference || "—";
                                      return (
                                        <tr key={p.id || pIdx}>
                                          <td className="ps-3 py-2 fw-semibold text-gray-900">
                                            {fmtDateTime(p.paidAt || p.paymentDate || p.createdDate)}
                                          </td>
                                          <td className="py-2 fw-bolder text-success fs-6">
                                            {fmtMoney(p.amount || p.amountPaid)}
                                          </td>
                                          <td className="py-2 text-gray-700">
                                            {p.paymentMethod || p.method || "Bank Transfer"}
                                          </td>
                                          <td className="py-2">
                                            {ref !== "—" ? (
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-light py-1 px-2 d-inline-flex align-items-center gap-1 font-monospace fs-8"
                                                onClick={() => copyToClipboard(ref)}
                                                title="Copy reference"
                                              >
                                                <span>{ref}</span>
                                                <Copy className="w-3 h-3 text-muted" />
                                              </button>
                                            ) : (
                                              <span className="text-muted fs-8">—</span>
                                            )}
                                          </td>
                                          <td className="pe-3 py-2 text-end">
                                            <span className="badge badge-light-success fs-8">
                                              {p.status || "Successful"}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>

                      </div>

                      {/* Modal Footer */}
                      <div className="modal-footer border-top py-3 px-6 bg-light d-flex justify-content-between">
                        {selectedRecord.tenantId && (
                          <Link
                            to={`/rent-details/${selectedRecord.tenantId}`}
                            className="btn btn-sm btn-outline btn-outline-primary d-inline-flex align-items-center gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            <span>Open Full Rent Management Page</span>
                          </Link>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary ms-auto"
                          onClick={() => setSelectedRecord(null)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
